import config from './config';
import { getToken, getOrgId } from './auth';

const base = config.apiEndpoint;

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': getToken(),
});

const req = async (method, path, body) => {
  // Pass orgId as query param to avoid CORS preflight issues with custom headers
  const orgId = getOrgId();
  const separator = path.includes('?') ? '&' : '?';
  const urlWithOrg = orgId ? `${base}${path}${separator}_orgId=${orgId}` : `${base}${path}`;
  const res = await fetch(urlWithOrg, {
    method,
    headers: authHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
};

// Auth / Me
export const getMe = () => req('GET', '/me');

// Orgs
export const createOrg = (data) => req('POST', '/orgs', data);
export const getOrg = () => req('GET', '/orgs/current');
export const updateOrg = (data) => req('PUT', '/orgs/current', data);
export const listOrgMembers = () => req('GET', '/orgs/members');
export const inviteMember = (data) => req('POST', '/orgs/invite', data);
export const removeMember = (userId) => req('DELETE', `/orgs/members/${userId}`);

// Invites
export const acceptInvite = (token) => req('POST', '/invites/accept', { token });

// Projects
export const getProjects = () => req('GET', '/projects');
export const createProject = (data) => req('POST', '/projects', data);
export const updateProject = (id, data) => req('PUT', `/projects/${id}`, data);
export const deleteProject = (id) => req('DELETE', `/projects/${id}`);

// Players
export const getPlayers = (projectId) => req('GET', `/players${projectId ? `?projectId=${projectId}` : ''}`);
export const getPlayer = (id) => req('GET', `/players/${id}`);
export const createPlayer = (data) => req('POST', '/players', data);
export const updatePlayer = (id, data) => req('PUT', `/players/${id}`, data);
export const deletePlayer = (id) => req('DELETE', `/players/${id}`);

// Transactions
export const addTransaction = (playerId, data) => req('POST', `/players/${playerId}/transactions`, data);
export const updateTransaction = (playerId, txnId, data) => req('PUT', `/players/${playerId}/transactions/${txnId}`, data);
export const deleteTransaction = (playerId, txnId) => req('DELETE', `/players/${playerId}/transactions/${txnId}`);

// Schedules
export const createSchedule = (playerId, data) => req('POST', `/players/${playerId}/schedules`, data);
export const updateSchedule = (playerId, scheduleId, data) => req('PUT', `/players/${playerId}/schedules/${scheduleId}`, data);
export const deleteSchedule = (playerId, scheduleId) => req('DELETE', `/players/${playerId}/schedules/${scheduleId}`);

// Communication
export const sendReminder = (playerId, message) => req('POST', `/players/${playerId}/remind`, { message });
export const notifyTransaction = (playerId, type, amount, description) => {
  const typeLabel = { payment: 'Payment received', expense: 'Charge added', adjustment: 'Account adjustment', credit: 'Credit applied' }[type] || type;
  const msg = `${typeLabel}: $${parseFloat(amount).toFixed(2)}${description ? ` — ${description}` : ''}`;
  return req('POST', `/players/${playerId}/remind`, { message: msg });
};
export const sendWelcome = (playerId, message) => req('POST', `/players/${playerId}/welcome`, { message });
export const shareWithEmails = (playerId, emails) => req('POST', `/players/${playerId}/share`, { emails });

// Settings
export const getSettings = () => req('GET', '/settings');
export const saveSettings = (data) => req('PUT', '/settings', data);

// Project actions
export const bulkRemind = (projectId, message) => req('POST', `/projects/${projectId}/bulk-remind`, { message });
export const rolloverProject = (projectId, data) => req('POST', `/projects/${projectId}/rollover`, data);
export const setAnnouncement = (projectId, text) => req('POST', `/projects/${projectId}/announcement`, { text });
export const setRecurringReminder = (projectId, data) => req('PUT', `/projects/${projectId}/recurring-reminder`, data);
export const exportProject = (projectId) => req('GET', `/projects/${projectId}/export`);

// Stripe
export const stripeConnectUrl = () => req('GET', '/stripe/connect-url');
export const stripeStatus = () => req('GET', '/stripe/status');
export const stripeCheckout = (data) => {
  // Public — no auth needed
  return fetch(`${base}/stripe/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(r => r.json());
};

// Super Admin
export const adminListOrgs = () => req('GET', '/admin/orgs');

// Public share view (no auth)
export const getShareView = async (token) => {
  const res = await fetch(`${base}/share/${token}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Not found');
  return data;
};
